import { Injectable, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  isUnpaidAccessDeniedSource,
  isUnpaidProductAuthorized,
} from './unpaid-payments-access.util';
import {
  UNPAID_HUB_PRODUCTS,
  UnpaidHubProductDefinition,
} from './unpaid-payments-hub-products.config';
import { UnpaidPaymentsIsDummyService } from './unpaid-payments-is-dummy.service';
import { readUnpaidPaymentsRouteProductKeys } from './unpaid-payments-route.config';

const ACCESS_CACHE_TTL_MS = 10 * 60 * 1000;
const SESSION_ACCESS_CACHE_PREFIX = 'unpaid_hub_access_v1';

interface SessionAccessCacheEntry {
  expiresAt: number;
  authorized: boolean;
}

const SIDEBAR_LABEL_BY_KEY: Record<string, string> = {
  denefits: 'Denefits',
  credee: 'Credee',
  lendee: 'Lendee',
  recuvery: 'Recuvery',
  fm: 'FinanceMutual',
};

const PRODUCT_KEY_BY_SIDEBAR_LABEL: Record<string, string> = {
  Denefits: 'denefits',
  Benefits: 'denefits',
  Credee: 'credee',
  Lendee: 'lendee',
  Recuvery: 'recuvery',
  Recovery: 'recuvery',
  FinanceMutual: 'fm',
};

const ROUTE_SEGMENT_BY_KEY = Object.fromEntries(
  UNPAID_HUB_PRODUCTS.map((product) => [product.key, product.routeSegment])
) as Record<string, string>;

const ROUTE_KEY_BY_SEGMENT = Object.fromEntries(
  UNPAID_HUB_PRODUCTS.map((product) => [product.routeSegment, product.key])
) as Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class UnpaidPaymentsAccessService {
  private readonly unauthorizedKeys = signal<ReadonlySet<string>>(new Set());
  private readonly authorizedKeys = signal<ReadonlySet<string>>(new Set());
  private readonly probedKeys = signal<ReadonlySet<string>>(new Set());

  constructor(private readonly isDummyService: UnpaidPaymentsIsDummyService) {
    this.hydrateFromSessionCache();
  }

  readonly hasAnyAccess = computed(() => this.authorizedKeys().size > 0);

  readonly hasPartialAccess = computed(
    () => this.unauthorizedProducts().length > 0 && this.hasAnyAccess()
  );

  readonly unauthorizedProducts = computed<UnpaidHubProductDefinition[]>(() => {
    const routableKeys = readUnpaidPaymentsRouteProductKeys();
    return UNPAID_HUB_PRODUCTS.filter(
      (product) =>
        routableKeys.has(product.key) &&
        this.isProbed(product.key) &&
        !this.hasAccess(product.key)
    );
  });

  readonly allUnauthorized = computed(() => {
    const routableKeys = readUnpaidPaymentsRouteProductKeys();
    if (routableKeys.size === 0) {
      return false;
    }
    return [...routableKeys].every((key) => this.isProbed(key) && !this.hasAccess(key));
  });

  readonly probingComplete = computed(() => {
    const routableKeys = readUnpaidPaymentsRouteProductKeys();
    if (routableKeys.size === 0) {
      return true;
    }
    return [...routableKeys].every((key) => this.isProbed(key));
  });

  readonly probedCount = computed(() => this.probedKeys().size);

  evaluate(productKey: string, response?: unknown): void {
    if (isUnpaidProductAuthorized(response)) {
      this.markAuthorized(productKey);
      return;
    }
    this.markUnauthorized(productKey);
  }

  markUnauthorized(productKey: string): void {
    this.probedKeys.update((current) => new Set([...current, productKey]));
    this.unauthorizedKeys.update((current) => new Set([...current, productKey]));
    this.authorizedKeys.update((current) => {
      if (!current.has(productKey)) {
        return current;
      }
      const next = new Set(current);
      next.delete(productKey);
      return next;
    });
    this.writeSessionAccess(productKey, false);
  }

  markAuthorized(productKey: string): void {
    this.probedKeys.update((current) => new Set([...current, productKey]));
    this.authorizedKeys.update((current) => new Set([...current, productKey]));
    this.unauthorizedKeys.update((current) => {
      if (!current.has(productKey)) {
        return current;
      }
      const next = new Set(current);
      next.delete(productKey);
      return next;
    });
    this.writeSessionAccess(productKey, true);
  }

  hasAccess(productKey: string): boolean {
    return this.authorizedKeys().has(productKey);
  }

  isProbed(productKey: string): boolean {
    return this.probedKeys().has(productKey);
  }

  firstAccessibleRouteSegment(): string {
    const routableKeys = readUnpaidPaymentsRouteProductKeys();
    const match = UNPAID_HUB_PRODUCTS.find(
      (product) => routableKeys.has(product.key) && this.hasAccess(product.key)
    );
    return match?.routeSegment ?? 'overview';
  }

  sidebarLabelForKey(productKey: string): string {
    return SIDEBAR_LABEL_BY_KEY[productKey] ?? productKey;
  }

  productKeyForSidebarLabel(label: string): string | null {
    return PRODUCT_KEY_BY_SIDEBAR_LABEL[label] ?? null;
  }

  routeSegmentForKey(productKey: string): string | null {
    return ROUTE_SEGMENT_BY_KEY[productKey] ?? null;
  }

  productKeyForRouteSegment(segment: string): string | null {
    return ROUTE_KEY_BY_SEGMENT[segment] ?? null;
  }

  unauthorizedProductLabels(): string[] {
    return this.unauthorizedProducts().map((product) => product.label);
  }

  /** Evaluates access from an API response. Returns false when the product is denied. */
  evaluateResponse(productKey: string, response: unknown): boolean {
    this.evaluate(productKey, response);
    return this.hasAccess(productKey);
  }

  isAccessDenied(source: unknown): boolean {
    return isUnpaidAccessDeniedSource(source);
  }

  redirectToOverview(router: Router, route: ActivatedRoute): void {
    void router.navigate(['../overview'], { relativeTo: route, replaceUrl: true });
  }

  handleAccessDenied(productKey: string, router: Router, route: ActivatedRoute): void {
    this.markUnauthorized(productKey);
    this.redirectToOverview(router, route);
  }

  private hydrateFromSessionCache(): void {
    const authorized = new Set<string>();
    const unauthorized = new Set<string>();
    const probed = new Set<string>();

    for (const product of UNPAID_HUB_PRODUCTS) {
      const entry = this.readSessionAccess(product.key);
      if (!entry) {
        continue;
      }
      probed.add(product.key);
      if (entry.authorized) {
        authorized.add(product.key);
      } else {
        unauthorized.add(product.key);
      }
    }

    this.authorizedKeys.set(authorized);
    this.unauthorizedKeys.set(unauthorized);
    this.probedKeys.set(probed);
  }

  private sessionCacheKey(productKey: string): string {
    return `${SESSION_ACCESS_CACHE_PREFIX}:${this.isDummyService.getStorageValue()}:${productKey}`;
  }

  private readSessionAccess(productKey: string): SessionAccessCacheEntry | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(this.sessionCacheKey(productKey));
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as SessionAccessCacheEntry;
      if (
        !parsed ||
        typeof parsed.expiresAt !== 'number' ||
        typeof parsed.authorized !== 'boolean'
      ) {
        sessionStorage.removeItem(this.sessionCacheKey(productKey));
        return null;
      }
      if (Date.now() >= parsed.expiresAt) {
        sessionStorage.removeItem(this.sessionCacheKey(productKey));
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem(this.sessionCacheKey(productKey));
      return null;
    }
  }

  private writeSessionAccess(productKey: string, authorized: boolean): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const payload: SessionAccessCacheEntry = {
        expiresAt: Date.now() + ACCESS_CACHE_TTL_MS,
        authorized,
      };
      sessionStorage.setItem(this.sessionCacheKey(productKey), JSON.stringify(payload));
    } catch {
      // Quota exceeded or private mode — in-memory state still applies for this session.
    }
  }
}
